import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { sanitizeInput } from '@/lib/security';
import { getAlternateLanguageUrl, detectLanguageFromPath } from '@/lib/routes';
import { SITE_CONFIG } from '@/config/site';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  type?: string;
  noindex?: boolean;
  jsonLd?: object | object[];
}

const BASE_URL = SITE_CONFIG.url;

// SEO best practices constants
const MAX_TITLE_LENGTH = 60; // Recommended: 50-60 characters
const MAX_DESCRIPTION_LENGTH = 160; // Recommended: 120-160 characters
const MIN_DESCRIPTION_LENGTH = 150; // Minimum for good SEO (Google recommends 150-160)

/**
 * Warn during development when a title or description is outside the
 * recommended range. Deliberately does NOT alter the value.
 */
function warnIfOutsideRange(kind: string, value: string, min: number, max: number) {
  if (typeof window === 'undefined' || !import.meta.env.DEV) return;
  if (value.length > max) {
    console.warn(`[SEO] ${kind} is ${value.length} chars (recommended max ${max}): "${value}"`);
  } else if (min && value.length < min) {
    console.warn(`[SEO] ${kind} is ${value.length} chars (recommended min ${min}): "${value}"`);
  }
}

/**
 * Normalises whitespace on the meta description.
 *
 * It does NOT truncate. Google decides how much of a description to show, and
 * cutting the tag ourselves both discards text and — because
 * verify-meta-descriptions.ts compares the rendered HTML against the untruncated
 * source string — made every description over 160 characters permanently fail
 * verification.
 */
function optimizeDescription(description: string): string {
  if (!description) return '';
  const cleaned = description.trim().replace(/\s+/g, ' ');
  warnIfOutsideRange('description', cleaned, MIN_DESCRIPTION_LENGTH, MAX_DESCRIPTION_LENGTH);
  return cleaned;
}

/**
 * Normalises whitespace on the title.
 *
 * It does NOT truncate. Callers compose titles as `${title} | Byteet`, so
 * cutting at MAX_TITLE_LENGTH removed the brand suffix from every page on the
 * site and could sever a word mid-token, leaving a literal "..." in the <title>.
 * Keep titles short at the source instead (articles carry a `seoTitle`);
 * verify-meta-descriptions.ts fails on article titles over 60 characters.
 */
function optimizeTitle(title: string): string {
  if (!title) return '';
  const cleaned = title.trim().replace(/\s+/g, ' ');
  warnIfOutsideRange('title', cleaned, 0, MAX_TITLE_LENGTH);
  return cleaned;
}

/**
 * Validates URL to prevent XSS and ensure it's a valid absolute URL
 */
function validateUrl(url: string): string {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    // Only allow https/http protocols
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      return '';
    }
    return url;
  } catch {
    // If relative URL, make it absolute
    if (url.startsWith('/')) {
      return `${BASE_URL}${url}`;
    }
    return '';
  }
}

const SEO = ({
  title,
  description,
  image = `${BASE_URL}/og_image.jpg?v=2`,
  type = 'website',
  noindex = false,
  jsonLd,
}: SEOProps) => {
  const location = useLocation();
  
  // Memoize sanitized and optimized values to avoid recalculation
  const sanitizedValues = useMemo(() => {
    const sanitizedTitle = title ? optimizeTitle(sanitizeInput(title)) : '';
    const sanitizedDescription = description ? optimizeDescription(sanitizeInput(description)) : '';
    const sanitizedImage = image ? validateUrl(sanitizeInput(image)) : `${BASE_URL}/og_image.jpg?v=2`;
    const sanitizedType = type ? sanitizeInput(type) : 'website';
    const currentUrl = `${BASE_URL}${location.pathname}`;
    
    return {
      title: sanitizedTitle,
      description: sanitizedDescription,
      image: sanitizedImage,
      type: sanitizedType,
      url: currentUrl,
    };
  }, [title, description, image, type, location.pathname]);

  // Track previous values to avoid unnecessary DOM updates
  const prevValuesRef = useRef<typeof sanitizedValues | null>(null);

  // Update meta tags immediately (synchronously) for SSG pre-rendering
  // This ensures meta tags are available when Puppeteer captures the HTML
  if (typeof document !== 'undefined') {
    const values = sanitizedValues;
    const prev = prevValuesRef.current;
    
    // Only update if values changed
    if (!prev || prev.title !== values.title) {
      if (values.title) {
        document.title = values.title;
        updateMetaTag('property', 'og:title', values.title);
        updateMetaTag('name', 'twitter:title', values.title);
      }
    }

    if (!prev || prev.description !== values.description) {
      if (values.description) {
        updateMetaTag('name', 'description', values.description);
        updateMetaTag('property', 'og:description', values.description);
        updateMetaTag('name', 'twitter:description', values.description);
      }
    }

    if (!prev || prev.url !== values.url) {
      updateLinkTag('canonical', values.url);
      updateMetaTag('property', 'og:url', values.url);
    }

    if (!prev || prev.type !== values.type) {
      updateMetaTag('property', 'og:type', values.type);
    }

    if (!prev || prev.image !== values.image) {
      if (values.image) {
        updateMetaTag('property', 'og:image', values.image);
        updateMetaTag('name', 'twitter:image', values.image);
      }
    }

    // Always update robots (simple boolean check)
    const robotsValue = noindex 
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
    updateMetaTag('name', 'robots', robotsValue);
    
    // Update og:locale based on language
    const currentLanguage = detectLanguageFromPath(location.pathname);
    const ogLocale = currentLanguage === 'nl' ? 'nl_NL' : 'en_US';
    const ogLocaleAlternate = currentLanguage === 'nl' ? 'en_US' : 'nl_NL';
    updateMetaTag('property', 'og:locale', ogLocale);
    updateMetaTag('property', 'og:locale:alternate', ogLocaleAlternate);

    // Update document lang attribute
    document.documentElement.setAttribute('lang', currentLanguage);

    prevValuesRef.current = values;
  }

  // Use useEffect for JSON-LD (only needs to run once per route change)
  useEffect(() => {
    if (!jsonLd) return;

    // Remove existing JSON-LD scripts
    const existingScripts = document.querySelectorAll('[id^="dynamic-json-ld"]');
    existingScripts.forEach(script => script.remove());

    // Validate and sanitize JSON-LD before injection
    const scripts = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    scripts.forEach((schema, index) => {
      try {
        // Validate it's a valid object
        if (typeof schema !== 'object' || schema === null) {
          console.warn('Invalid JSON-LD schema:', schema);
          return;
        }

        // Stringify and parse to ensure it's safe
        const jsonString = JSON.stringify(schema);
        const parsed = JSON.parse(jsonString); // Re-parse to ensure it's valid JSON

        const script = document.createElement('script');
        script.id = `dynamic-json-ld-${index}`;
        script.type = 'application/ld+json';
        // Use textContent instead of innerHTML for better security
        script.textContent = jsonString;
        document.head.appendChild(script);
      } catch (error) {
        console.error('Error adding JSON-LD schema:', error);
      }
    });
  }, [jsonLd, location.pathname]); // Only re-run when jsonLd or route changes

  // Compute hreflang URLs for rendering as React elements (visible in SSG pre-rendered HTML)
  const hreflangData = useMemo(() => {
    const englishPath = getAlternateLanguageUrl(location.pathname, 'en');
    const dutchPath = getAlternateLanguageUrl(location.pathname, 'nl');

    return {
      enUrl: `${BASE_URL}${englishPath}`,
      nlUrl: `${BASE_URL}${dutchPath}`,
      xDefaultUrl: `${BASE_URL}${englishPath}`,
    };
  }, [location.pathname]);

  // Render hreflang link elements as React JSX so they appear in SSG pre-rendered HTML
  // Using createPortal to inject into <head> ensures Puppeteer captures them
  // A noindex page (404, not-found states) has no language alternates to announce.
  if (typeof document === 'undefined' || noindex) return null;

  return createPortal(
    <>
      <link rel="alternate" hrefLang="en" href={hreflangData.enUrl} />
      <link rel="alternate" hrefLang="nl" href={hreflangData.nlUrl} />
      <link rel="alternate" hrefLang="x-default" href={hreflangData.xDefaultUrl} />
    </>,
    document.head
  );
};

// Helper function to update or create meta tags
// Caches selectors to avoid repeated queries
const metaTagCache = new Map<string, HTMLMetaElement>();

function updateMetaTag(attribute: 'name' | 'property', key: string, value: string): void {
  if (typeof document === 'undefined' || !value) return;

  const cacheKey = `${attribute}-${key}`;
  let element = metaTagCache.get(cacheKey);

  if (!element) {
    const selector = attribute === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
    element = document.querySelector(selector) as HTMLMetaElement;

    if (!element) {
      element = document.createElement('meta');
      if (attribute === 'name') {
        element.setAttribute('name', key);
      } else {
        element.setAttribute('property', key);
      }
      document.head.appendChild(element);
    }

    metaTagCache.set(cacheKey, element);
  }

  // Only update if value changed
  if (element.getAttribute('content') !== value) {
    element.setAttribute('content', value);
  }
}

// Helper function to update or create link tags
// Caches link elements to avoid repeated queries
const linkTagCache = new Map<string, HTMLLinkElement>();

function updateLinkTag(rel: string, href: string, id?: string, hreflang?: string): void {
  if (typeof document === 'undefined' || !href) return;

  const cacheKey = id || rel;
  let element = linkTagCache.get(cacheKey);

  if (!element) {
    // For hreflang tags, we need to find by rel and hreflang
    if (hreflang) {
      element = document.querySelector(`link[rel="${rel}"][hreflang="${hreflang}"]`) as HTMLLinkElement;
    } else {
      element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
    }

    if (!element) {
      element = document.createElement('link');
      element.setAttribute('rel', rel);
      if (hreflang) {
        element.setAttribute('hreflang', hreflang);
      }
      if (id) {
        element.id = id;
      }
      document.head.appendChild(element);
    }

    linkTagCache.set(cacheKey, element);
  }

  // Only update if href changed
  if (element.getAttribute('href') !== href) {
    element.setAttribute('href', href);
  }
  
  // Update hreflang if provided
  if (hreflang && element.getAttribute('hreflang') !== hreflang) {
    element.setAttribute('hreflang', hreflang);
  }
}

export default SEO;

